import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { EMPTY, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { ApiService, ApiEndpoints } from '@nano/core/api';
import { AuthService, isTrustedApiOnboardedCorporate } from '@nano/core/auth';
import { ErrorService } from '@nano/core/error';
import {
  VerificationStartResponse,
  Verification,
  VERIFICATION_STORAGE_KEY,
  VerificationPaymentStage,
  KycRenewalStatus,
  VerificationStatus
} from './verification-status';
import { indicateLoading } from '../common/util/indicate-loading';

@Injectable({ providedIn: 'root' })
export class VerificationStatusService {
  loading$: Observable<boolean>;

  private loadingSubject: Subject<boolean>;
  private verification$?: Observable<Verification>;
  private verificationRequestInFlight = false;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private errorService: ErrorService
  ) {
    this.loadingSubject = new Subject();
    this.loading$ = this.loadingSubject.asObservable();
  }

  startVerification(stages?: string[]): Observable<string> {
    if (!stages) {
      stages = this.getVerificationFromStorage().stages;
    }
    const body = stages ? { stages } : {};
    return this.apiService.post<VerificationStartResponse>(ApiEndpoints.verification(), body).pipe(
      indicateLoading(this.loadingSubject),
      map((response: VerificationStartResponse) => {
        return response.redirectUrl;
      }),
      catchError((error: unknown) => {
        this.errorService.handleRequestError(error);
        return EMPTY;
      })
    );
  }

  startKycRenewal(): Observable<string> {
    return this.apiService.post<VerificationStartResponse>(ApiEndpoints.verification(), { renewal: true }).pipe(
      indicateLoading(this.loadingSubject),
      map((response: VerificationStartResponse) => {
        return response.redirectUrl;
      }),
      catchError((error: unknown) => {
        this.errorService.handleRequestError(error);
        return EMPTY;
      })
    );
  }

  getVerification(): Observable<Verification> {
    if (!this.verification$) {
      this.verification$ = this.createVerificationRequest();
    }

    return this.verification$;
  }

  refreshVerification(): Observable<Verification> {
    if (!this.verificationRequestInFlight) {
      this.verification$ = undefined;
    }

    return this.getVerification();
  }

  getVerificationFromStorage(): Verification {
    return JSON.parse(sessionStorage.getItem(VERIFICATION_STORAGE_KEY) || '{}') as Verification;
  }

  getVerificationPaymentStage(operationId?: string): Observable<VerificationPaymentStage> {
    if (this.isTrustedApiOnboardedCorporate()) {
      return of({} as VerificationPaymentStage);
    }

    const params: HttpParams = new HttpParams().set('paymentEntryId', operationId);
    return operationId
      ? this.apiService.get<VerificationPaymentStage>(ApiEndpoints.verificationPaymentStage(), params)
      : this.apiService.get<VerificationPaymentStage>(ApiEndpoints.verificationPaymentStage());
  }

  isRenewalStatus(verification: Verification): boolean {
    const isKycStatusApprovedCondition =
      verification.kycRenewalStatus &&
      verification.kycRenewalStatus !== KycRenewalStatus.VALID &&
      !verification.failCritical &&
      verification.status === VerificationStatus.APPROVED &&
      verification.authorisedForThisUser;

    const isKycStatusCreatedCondition =
      verification.needToAcknowledge &&
      (!verification.kycRenewalStatus || verification.kycRenewalStatus === KycRenewalStatus.VALID) &&
      !verification.failCritical &&
      verification.status === VerificationStatus.CREATED &&
      verification.authorisedForThisUser;

    return isKycStatusApprovedCondition || isKycStatusCreatedCondition;
  }

  private createVerificationRequest(): Observable<Verification> {
    if (this.isTrustedApiOnboardedCorporate()) {
      return of(this.createTrustedApiVerification()).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }

    this.verificationRequestInFlight = true;

    return this.apiService.get<Verification>(ApiEndpoints.verification()).pipe(
      catchError((error: unknown) => {
        this.verification$ = undefined;
        return throwError(() => error);
      }),
      finalize(() => {
        this.verificationRequestInFlight = false;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  private isTrustedApiOnboardedCorporate(): boolean {
    return isTrustedApiOnboardedCorporate(this.authService.userProfile);
  }

  private createTrustedApiVerification(): Verification {
    return {
      status: VerificationStatus.APPROVED,
      success: true,
      kycRenewalStatus: KycRenewalStatus.VALID,
      failCritical: false,
      authorisedForThisUser: true,
      alreadyStarted: false,
      alreadyStartedByThisUser: false,
      needToAcknowledge: false,
      stages: []
    } as Verification;
  }
}
